import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Alert,
  Grid,
  LinearProgress,
  Autocomplete,
  Collapse,
  Tooltip,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Restaurant as RestaurantIcon,
  Visibility as VisibilityIcon,
  Repeat as RepeatIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AssignmentTurnedIn as AutoAssignIcon,
} from '@mui/icons-material';
import {
  useFeedTypes,
  useFeedBrands,
  useRawMaterials,
  useFeedProductions,
  useProduceFeed,
  useUpdateFeedProduction,
  useDeleteFeedProduction,
  useFeedProductionDetail,
  useAutoAssignProduction,
  useFeedAssignments,
} from '../hooks/useFeed';
import { useBirdProcurements } from '../hooks/useBirds';
import { BirdProcurement } from '../types';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/format';

interface MaterialInput {
  raw_material_id: number;
  quantity: number;
}

interface ProductionSummaryItem {
  feedType: string;
  feedBrand: string;
  totalBags: number;
  totalKg: number;
  totalCost: number;
  productionCount: number;
}

interface OverallTotals {
  totalBags: number;
  totalKg: number;
  totalCost: number;
  productionCount: number;
}

interface RawMaterialSummaryItem {
  materialId: number;
  materialName: string;
  totalQuantity: number;
  unit: string;
  totalCost: number;
}

// ── Stat card ──────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: string;
}> = ({ label, value, sub, accent = '#ff9800' }) => (
  <Paper
    sx={{
      p: 2,
      background: 'linear-gradient(135deg, #424242 0%, #303030 100%)',
      border: '1px solid #555',
      height: '100%',
    }}
  >
    <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: accent, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: '#888', mt: 0.5, display: 'block' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ── Per-feed-type breakdown ────────────────────────────────────────────────────

const ProductionBreakdownSection: React.FC<{
  productionSummary: ProductionSummaryItem[];
  hasActiveFilters: boolean;
}> = ({ productionSummary, hasActiveFilters }) => {
  const [expanded, setExpanded] = useState(false);

  if (productionSummary.length === 0) return null;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: 1.5,
          '&:hover': { opacity: 0.8 },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
          By Feed Type
          {hasActiveFilters && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              (filtered)
            </Typography>
          )}
        </Typography>
        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={expanded}>
        <Grid container spacing={1.5}>
          {productionSummary.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <Paper
                sx={{
                  p: 1.5,
                  background: 'linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%)',
                  border: '1px solid #444',
                }}
              >
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5 }}>
                  {item.feedBrand} · {item.feedType}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                  {item.totalBags.toFixed(1)} bags
                </Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {item.totalKg.toFixed(0)} kg · {formatCurrency(item.totalCost)} ·{' '}
                  {item.productionCount} run{item.productionCount !== 1 ? 's' : ''}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Box>
  );
};

// ── Raw materials summary ──────────────────────────────────────────────────────

const RawMaterialsUsedSection: React.FC<{
  rawMaterialsSummary: RawMaterialSummaryItem[];
}> = ({ rawMaterialsSummary }) => {
  const [expanded, setExpanded] = useState(true);

  if (rawMaterialsSummary.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          mb: 1.5,
          '&:hover': { opacity: 0.8 },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
          Raw Materials Used
        </Typography>
        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      <Collapse in={expanded}>
        <Grid container spacing={1.5}>
          {rawMaterialsSummary.map((material) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={material.materialId}>
              <Paper
                sx={{
                  p: 1.5,
                  background: 'linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%)',
                  border: '1px solid #444',
                }}
              >
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 0.5 }}>
                  {material.materialName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                    {material.totalQuantity.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#bbb' }}>
                    {material.unit}
                  </Typography>
                </Box>
                {material.unit.toLowerCase() === 'kg' && (
                  <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                    {(material.totalQuantity / 50).toFixed(2)} bags ·{' '}
                    {formatCurrency(material.totalCost)}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Collapse>
    </Box>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

function getLastTuesday(): string {
  const d = new Date();
  const daysBack = (d.getDay() + 5) % 7;
  d.setDate(d.getDate() - daysBack);
  return format(d, 'yyyy-MM-dd');
}

const FeedProductionPage: React.FC = () => {
  const [filters, setFilters] = useState({
    feed_type_id: null as number | null,
    feed_brand_id: null as number | null,
    bird_procurement_id: null as number | null,
    date_from: getLastTuesday(),
    date_to: '',
  });

  const { data: feedTypes, isFetching: feedTypesLoading } = useFeedTypes();
  const { data: feedBrands, isFetching: feedBrandsLoading } = useFeedBrands();
  const { data: rawMaterials, isFetching: rawMaterialsLoading } = useRawMaterials();
  const { data: procurementsData, isFetching: procurementsLoading } = useBirdProcurements({
    per_page: 100,
  });
  const {
    data: productions,
    isLoading: productionsLoading,
    isFetching: productionsFetching,
  } = useFeedProductions({
    feed_type_id: filters.feed_type_id || undefined,
    feed_brand_id: filters.feed_brand_id || undefined,
    bird_procurement_id: filters.bird_procurement_id || undefined,
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
  });
  const produceMutation = useProduceFeed();
  const updateMutation = useUpdateFeedProduction();
  const deleteMutation = useDeleteFeedProduction();
  const autoAssignMutation = useAutoAssignProduction();

  const [editingProductionId, setEditingProductionId] = useState<number | null>(null);

  const { data: assignmentsData } = useFeedAssignments({ per_page: 200, is_active: 1 });
  const assignments: any[] = (assignmentsData as any)?.data ?? (assignmentsData as any) ?? [];

  const nextProductionByType = React.useMemo(() => {
    // Group by feed_production_id so batches sharing the same production (e.g. 2nd+3rd, 5th+6th)
    // appear as a single card. production_days_remaining is null for productions older than 7 days.
    const procurementCardCount = new Map<number, number>();
    const map = new Map<
      number,
      {
        procurementLabels: string[];
        birdNames: string[];
        feedLabel: string;
        daysRemaining: number;
        date: Date;
        procurementId: number | null;
      }
    >();
    for (const a of assignments) {
      if (a.production_days_remaining == null || !a.feed_production_id) continue;
      const days = parseFloat(a.production_days_remaining);
      const pid: number = a.feed_production_id;
      const procLabel: string = a.bird_procurement?.identifier ?? `Batch #${a.bird_procurement_id}`;
      const birdName: string | null = a.birds?.name ?? null;
      const existing = map.get(pid);
      if (!existing) {
        const date = new Date();
        date.setDate(date.getDate() + Math.floor(days));
        map.set(pid, {
          procurementLabels: [procLabel],
          birdNames: birdName ? [birdName] : [],
          feedLabel: `${a.feed_type?.name ?? '?'} – ${a.feed_brand?.name ?? '?'}`,
          daysRemaining: days,
          date,
          procurementId: a.bird_procurement_id ?? null,
        });
        const prev = procurementCardCount.get(a.bird_procurement_id) ?? 0;
        procurementCardCount.set(a.bird_procurement_id, prev + 1);
      } else {
        if (!existing.procurementLabels.includes(procLabel)) {
          existing.procurementLabels.push(procLabel);
        }
        if (birdName && !existing.birdNames.includes(birdName)) {
          existing.birdNames.push(birdName);
        }
      }
    }
    // When multiple productions share the same procurement, qualify with bird name only
    // if exactly one bird group is assigned (multiple means the full batch is covered).
    const entries = Array.from(map.values()).map((item) => {
      const sharedProcurement =
        item.procurementId != null && (procurementCardCount.get(item.procurementId) ?? 0) > 1;
      const singleBird = item.birdNames.length === 1 ? item.birdNames[0] : null;
      return {
        ...item,
        procurementLabels:
          sharedProcurement && singleBird
            ? item.procurementLabels.map((l) => `${l} · ${singleBird}`)
            : item.procurementLabels,
      };
    });
    return entries.sort((a, b) => a.daysRemaining - b.daysRemaining);
  }, [assignments]);

  const [productionDialogOpen, setProductionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [autoAssignSnackbar, setAutoAssignSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [productionToDelete, setProductionToDelete] = useState<number | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedProductionId, setSelectedProductionId] = useState<number>(0);
  const [formData, setFormData] = useState({
    feed_type_id: 0,
    feed_brand_id: 0,
    bird_procurement_id: null as number | null,
    quantity_kg: '',
    other_costs: '',
    production_date: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
  });
  const [materials, setMaterials] = useState<MaterialInput[]>([
    { raw_material_id: 0, quantity: 0 },
  ]);

  const addMaterial = () => setMaterials([...materials, { raw_material_id: 0, quantity: 0 }]);
  const removeMaterial = (index: number) => setMaterials(materials.filter((_, i) => i !== index));
  const updateMaterial = (index: number, field: keyof MaterialInput, value: any) => {
    const updated = [...materials];
    updated[index] = { ...updated[index], [field]: value };
    setMaterials(updated);
  };

  const resetDialog = () => {
    setProductionDialogOpen(false);
    setEditingProductionId(null);
    setFormData({
      feed_type_id: 0,
      feed_brand_id: 0,
      bird_procurement_id: null,
      quantity_kg: '',
      other_costs: '',
      production_date: format(new Date(), 'yyyy-MM-dd'),
      notes: '',
    });
    setMaterials([{ raw_material_id: 0, quantity: 0 }]);
  };

  const handleProduceFeed = async () => {
    const payload = {
      feed_type_id: formData.feed_type_id,
      feed_brand_id: formData.feed_brand_id,
      bird_procurement_id: formData.bird_procurement_id || undefined,
      quantity_kg: parseFloat(formData.quantity_kg),
      materials: materials.map((m) => {
        const rm = rawMaterials?.find((r: any) => r.id === m.raw_material_id);
        const topUpKg = parseFloat(rm?.top_up_kg ?? 0) || 0;
        const kgPerBag = parseFloat(rm?.default_kg_per_bag ?? 50) || 50;
        const topUpTotal = topUpKg > 0 ? (m.quantity / kgPerBag) * topUpKg : 0;
        return { raw_material_id: m.raw_material_id, quantity: m.quantity + topUpTotal };
      }),
      other_costs: parseFloat(formData.other_costs),
      production_date: formData.production_date,
      notes: formData.notes || undefined,
    };

    try {
      if (editingProductionId) {
        await updateMutation.mutateAsync({ id: editingProductionId, data: payload });
      } else {
        await produceMutation.mutateAsync(payload);
      }
      resetDialog();
    } catch (error: any) {
      console.error('Failed to save feed production:', error);
      alert(error.response?.data?.message || 'Failed to save feed production');
    }
  };

  const handleDeleteClick = (productionId: number) => {
    setProductionToDelete(productionId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productionToDelete) return;
    try {
      await deleteMutation.mutateAsync(productionToDelete);
      setDeleteDialogOpen(false);
      setProductionToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete production:', error);
      alert(error.response?.data?.message || 'Failed to delete production');
    }
  };

  const handleViewDetail = (productionId: number) => {
    setSelectedProductionId(productionId);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedProductionId(0);
  };

  const handleRepeatProduction = (production: any) => {
    setFormData({
      feed_type_id: production.feed_type_id,
      feed_brand_id: production.feed_brand_id,
      bird_procurement_id: production.bird_procurement_id || null,
      quantity_kg: production.quantity_produced_kg.toString(),
      other_costs: production.other_costs.toString(),
      production_date: format(new Date(), 'yyyy-MM-dd'),
      notes: production.notes || '',
    });
    if (production.materials && production.materials.length > 0) {
      setMaterials(
        production.materials.map((m: any) => ({
          raw_material_id: m.raw_material_id,
          quantity: parseFloat(m.quantity_used),
        }))
      );
    } else {
      setMaterials([{ raw_material_id: 0, quantity: 0 }]);
    }
    setProductionDialogOpen(true);
  };

  const handleEditClick = (production: any) => {
    setEditingProductionId(production.id);
    setFormData({
      feed_type_id: production.feed_type_id,
      feed_brand_id: production.feed_brand_id,
      bird_procurement_id: production.bird_procurement_id || null,
      quantity_kg: production.quantity_produced_kg.toString(),
      other_costs: production.other_costs.toString(),
      production_date: production.production_date,
      notes: production.notes || '',
    });
    if (production.materials && production.materials.length > 0) {
      setMaterials(
        production.materials.map((m: any) => ({
          raw_material_id: m.raw_material_id,
          quantity: parseFloat(m.quantity_used),
        }))
      );
    } else {
      setMaterials([{ raw_material_id: 0, quantity: 0 }]);
    }
    setProductionDialogOpen(true);
  };

  const handleAutoAssign = (productionId: number) => {
    autoAssignMutation.mutate(productionId, {
      onSuccess: (result: any) => {
        const count = result.assignments?.length ?? 0;
        setAutoAssignSnackbar({
          open: true,
          message: result.message,
          severity: count > 0 ? 'success' : 'info',
        });
      },
      onError: (error: any) => {
        setAutoAssignSnackbar({
          open: true,
          message: error?.response?.data?.message || 'Auto-assign failed',
          severity: 'error',
        });
      },
    });
  };

  const procurements = procurementsData?.data ?? [];

  const isFetching =
    productionsFetching ||
    feedTypesLoading ||
    feedBrandsLoading ||
    rawMaterialsLoading ||
    procurementsLoading ||
    produceMutation.isPending ||
    deleteMutation.isPending;

  const totalMaterialCost = materials.reduce((sum, m) => {
    const material = rawMaterials?.find((rm: any) => rm.id === m.raw_material_id);
    return sum + (material ? parseFloat(material.average_cost_per_unit || 0) * m.quantity : 0);
  }, 0);

  const totalProductionCost = totalMaterialCost + parseFloat(formData.other_costs || '0');
  const costPerBag =
    formData.quantity_kg && parseFloat(formData.quantity_kg) > 0
      ? totalProductionCost / (parseFloat(formData.quantity_kg) / 50)
      : 0;

  const handleClearFilters = () =>
    setFilters({
      feed_type_id: null,
      feed_brand_id: null,
      bird_procurement_id: null,
      date_from: '',
      date_to: '',
    });

  const hasActiveFilters = !!(
    filters.feed_type_id ||
    filters.feed_brand_id ||
    filters.bird_procurement_id ||
    filters.date_from ||
    filters.date_to
  );

  const productionSummary = React.useMemo(() => {
    if (!productions || productions.length === 0) return [];
    const summary = new Map<string, ProductionSummaryItem>();
    productions.forEach((production: any) => {
      const key = `${production.feed_type_id}-${production.feed_brand_id}`;
      const existing = summary.get(key);
      if (existing) {
        existing.totalBags += parseFloat(production.quantity_produced_bags);
        existing.totalKg += parseFloat(production.quantity_produced_kg);
        existing.totalCost += parseFloat(production.total_production_cost);
        existing.productionCount += 1;
      } else {
        summary.set(key, {
          feedType: production.feed_type?.name || 'Unknown',
          feedBrand: production.feed_brand?.name || 'Unknown',
          totalBags: parseFloat(production.quantity_produced_bags),
          totalKg: parseFloat(production.quantity_produced_kg),
          totalCost: parseFloat(production.total_production_cost),
          productionCount: 1,
        });
      }
    });
    return Array.from(summary.values());
  }, [productions]);

  const overallTotals = React.useMemo<OverallTotals>(() => {
    return productionSummary.reduce(
      (acc, item) => ({
        totalBags: acc.totalBags + item.totalBags,
        totalKg: acc.totalKg + item.totalKg,
        totalCost: acc.totalCost + item.totalCost,
        productionCount: acc.productionCount + item.productionCount,
      }),
      { totalBags: 0, totalKg: 0, totalCost: 0, productionCount: 0 }
    );
  }, [productionSummary]);

  const rawMaterialsSummary = React.useMemo<RawMaterialSummaryItem[]>(() => {
    if (!productions || productions.length === 0) return [];
    const summary = new Map<number, RawMaterialSummaryItem>();
    productions.forEach((production: any) => {
      production.materials?.forEach((material: any) => {
        const existing = summary.get(material.raw_material_id);
        if (existing) {
          existing.totalQuantity += parseFloat(material.quantity_used);
          existing.totalCost += parseFloat(material.total_cost);
        } else {
          summary.set(material.raw_material_id, {
            materialId: material.raw_material_id,
            materialName: material.raw_material?.name || 'Unknown',
            totalQuantity: parseFloat(material.quantity_used),
            unit: material.raw_material?.unit_of_measure || 'units',
            totalCost: parseFloat(material.total_cost),
          });
        }
      });
    });
    return Array.from(summary.values()).sort((a, b) =>
      a.materialName.localeCompare(b.materialName)
    );
  }, [productions]);

  const hasProductions = productions && productions.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Feed Production
          </Typography>
          {hasActiveFilters && (
            <Typography variant="caption" color="text.secondary">
              Showing filtered results
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<RestaurantIcon />}
          onClick={() => setProductionDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)' },
          }}
        >
          Produce Feed
        </Button>
      </Box>

      {/* Next Production Estimates */}
      {nextProductionByType.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              fontWeight: 700,
              fontSize: '0.65rem',
              display: 'block',
              mb: 1,
            }}
          >
            Next Production Needed
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {nextProductionByType.map((item) => {
              const days = Math.floor(item.daysRemaining);
              const urgent = days < 3;
              const soon = days < 7;
              const accent = urgent ? '#ef5350' : soon ? '#ffa726' : '#4caf50';
              const dateStr = item.date.toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              });
              return (
                <Paper
                  key={`${item.procurementLabels.join(',')}-${item.feedLabel}`}
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 1.25,
                    border: `1px solid ${accent}30`,
                    borderRadius: 2,
                    background: `${accent}0a`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: 200,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: accent,
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: '0.82rem' }}
                    >
                      {item.feedLabel}
                    </Typography>
                    <Typography
                      sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', mb: 0.25 }}
                    >
                      {item.procurementLabels.join(' · ')}
                    </Typography>
                    <Typography sx={{ color: accent, fontWeight: 700, fontSize: '0.78rem' }}>
                      {days === 0 ? 'Today' : `In ${days} day${days !== 1 ? 's' : ''}`}
                      <Typography
                        component="span"
                        sx={{
                          color: 'rgba(255,255,255,0.35)',
                          fontWeight: 400,
                          ml: 0.75,
                          fontSize: '0.74rem',
                        }}
                      >
                        · {dateStr}
                      </Typography>
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Always-visible stat cards */}
      {hasProductions && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              label="Total Productions"
              value={overallTotals.productionCount}
              accent="#42a5f5"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              label="Total Bags"
              value={overallTotals.totalBags.toFixed(1)}
              sub={`${overallTotals.totalKg.toFixed(0)} kg`}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              label="Total Cost"
              value={formatCurrency(overallTotals.totalCost)}
              accent="#4caf50"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard
              label="Avg Cost / Bag"
              value={
                overallTotals.totalBags > 0
                  ? formatCurrency(overallTotals.totalCost / overallTotals.totalBags)
                  : '—'
              }
              accent="#ab47bc"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters + breakdown + table */}
      <Paper>
        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={feedTypes || []}
                getOptionLabel={(option: any) => option.name}
                value={feedTypes?.find((t: any) => t.id === filters.feed_type_id) || null}
                onChange={(_, v) => setFilters({ ...filters, feed_type_id: v?.id || null })}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Feed Type" size="small" />
                )}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={feedBrands || []}
                getOptionLabel={(option: any) => option.name}
                value={feedBrands?.find((b: any) => b.id === filters.feed_brand_id) || null}
                onChange={(_, v) => setFilters({ ...filters, feed_brand_id: v?.id || null })}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Brand" size="small" />
                )}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label="From"
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                fullWidth
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                label="To"
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                fullWidth
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Autocomplete
                options={procurements}
                getOptionLabel={(option: BirdProcurement) => option.identifier}
                value={
                  procurements.find((p: BirdProcurement) => p.id === filters.bird_procurement_id) ||
                  null
                }
                onChange={(_, v) => setFilters({ ...filters, bird_procurement_id: v?.id || null })}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by Batch" size="small" />
                )}
                isOptionEqualToValue={(option: BirdProcurement, value: BirdProcurement) =>
                  option.id === value.id
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                fullWidth
                variant="outlined"
                size="medium"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Per-type breakdown + raw materials */}
        {hasProductions && (
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <ProductionBreakdownSection
              productionSummary={productionSummary}
              hasActiveFilters={hasActiveFilters}
            />
            <RawMaterialsUsedSection rawMaterialsSummary={rawMaterialsSummary} />
          </Box>
        )}

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 120 }}>Production #</TableCell>
                <TableCell>Feed</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Birds</TableCell>
                <TableCell>Materials</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productionsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : !hasProductions ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <RestaurantIcon
                      sx={{
                        fontSize: 40,
                        color: 'text.disabled',
                        display: 'block',
                        mx: 'auto',
                        mb: 1,
                      }}
                    />
                    <Typography color="text.secondary" gutterBottom>
                      No productions recorded yet
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setProductionDialogOpen(true)}
                      sx={{ mt: 1 }}
                    >
                      Record first production
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                productions.map((production: any) => (
                  <TableRow key={production.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {production.production_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {production.feed_brand?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {production.feed_type?.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {parseFloat(production.quantity_produced_bags).toFixed(1)} bags
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {parseFloat(production.quantity_produced_kg).toFixed(0)} kg
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(production.total_production_cost)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(production.cost_per_bag)} / bag
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {format(new Date(production.production_date), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      {production.bird_feed_assignments?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {(production.bird_feed_assignments as any[])
                            .filter(
                              (a: any, i: number, arr: any[]) =>
                                a.birds?.name &&
                                arr.findIndex((x: any) => x.birds?.id === a.birds?.id) === i
                            )
                            .map((a: any) => (
                              <Typography
                                key={a.birds.id}
                                variant="caption"
                                noWrap
                                sx={{ color: 'text.secondary', display: 'block' }}
                              >
                                {a.birds.name}
                              </Typography>
                            ))}
                        </Box>
                      ) : production.bird_procurement?.birds?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {(production.bird_procurement.birds as any[]).map((b: any) => (
                            <Typography
                              key={b.id}
                              variant="caption"
                              noWrap
                              sx={{ color: 'text.disabled', display: 'block' }}
                            >
                              {b.name}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {production.materials?.map((m: any) => (
                          <Typography
                            key={m.id}
                            variant="caption"
                            noWrap
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            {m.raw_material?.name ?? `#${m.raw_material_id}`}
                          </Typography>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton
                          onClick={() => handleViewDetail(production.id)}
                          color="primary"
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit production">
                        <IconButton
                          onClick={() => handleEditClick(production)}
                          color="info"
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Repeat production">
                        <IconButton
                          onClick={() => handleRepeatProduction(production)}
                          color="success"
                          size="small"
                        >
                          <RepeatIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Auto-assign to birds">
                        <span>
                          <IconButton
                            onClick={() => handleAutoAssign(production.id)}
                            color="warning"
                            size="small"
                            disabled={autoAssignMutation.isPending}
                          >
                            <AutoAssignIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            onClick={() => handleDeleteClick(production.id)}
                            color="error"
                            size="small"
                            disabled={deleteMutation.isPending}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Production Dialog */}
      <Dialog open={productionDialogOpen} onClose={resetDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingProductionId ? 'Edit Production' : 'Produce Feed'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 3,
              pb: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={feedBrands || []}
                  getOptionLabel={(option: any) => option.name}
                  value={feedBrands?.find((b: any) => b.id === formData.feed_brand_id) || null}
                  onChange={(_, v) => setFormData({ ...formData, feed_brand_id: v?.id || 0 })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Feed Brand"
                      required
                      error={!formData.feed_brand_id}
                    />
                  )}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={feedTypes || []}
                  getOptionLabel={(option: any) => option.name}
                  value={feedTypes?.find((t: any) => t.id === formData.feed_type_id) || null}
                  onChange={(_, v) => setFormData({ ...formData, feed_type_id: v?.id || 0 })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Feed Type"
                      required
                      error={!formData.feed_type_id}
                    />
                  )}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                />
              </Grid>
            </Grid>

            <Autocomplete
              options={procurements}
              getOptionLabel={(option: BirdProcurement) => option.identifier}
              value={
                procurements.find((p: BirdProcurement) => p.id === formData.bird_procurement_id) ||
                null
              }
              onChange={(_, v) => setFormData({ ...formData, bird_procurement_id: v?.id || null })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Producing For (Procurement)"
                  required
                  error={!formData.bird_procurement_id}
                />
              )}
              isOptionEqualToValue={(option: BirdProcurement, value: BirdProcurement) =>
                option.id === value.id
              }
            />

            <TextField
              label="Quantity to Produce (kg)"
              type="number"
              value={formData.quantity_kg}
              onChange={(e) => setFormData({ ...formData, quantity_kg: e.target.value })}
              fullWidth
              required
              helperText={`≈ ${(parseFloat(formData.quantity_kg || '0') / 50).toFixed(2)} bags`}
            />

            {/* Raw Materials */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Raw Materials
                </Typography>
                <Button startIcon={<AddIcon />} onClick={addMaterial} size="small">
                  Add Material
                </Button>
              </Box>

              {materials.map((material, index) => {
                const selectedMaterialIds = materials
                  .map((m, i) => (i !== index ? m.raw_material_id : null))
                  .filter((id) => id !== null && id !== 0);

                const availableMaterials = rawMaterials
                  ?.filter((rm: any) => !selectedMaterialIds.includes(rm.id) && !!rm.is_active)
                  .filter((rm: any) => {
                    if (!rm.name.toLowerCase().includes('concentrate')) return true;
                    const selectedFeedBrand = feedBrands?.find(
                      (b) => b.id === formData.feed_brand_id
                    );
                    if (selectedFeedBrand) {
                      const materialName = rm.name.toLowerCase().split('concentrate')[0].trim();
                      return materialName === selectedFeedBrand.name.toLowerCase();
                    }
                    return true;
                  });

                const selectedMaterial = rawMaterials?.find(
                  (rm: any) => rm.id === material.raw_material_id
                );
                const topUpKg = parseFloat(selectedMaterial?.top_up_kg ?? 0) || 0;
                const kgPerBag = parseFloat(selectedMaterial?.default_kg_per_bag ?? 50) || 50;
                const topUpTotal =
                  topUpKg > 0 && material.quantity > 0
                    ? (material.quantity / kgPerBag) * topUpKg
                    : 0;

                return (
                  <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Autocomplete
                        options={availableMaterials || []}
                        getOptionLabel={(option: any) =>
                          `${option.name} (Stock: ${parseFloat(option.current_stock).toFixed(2)} ${option.unit_of_measure})`
                        }
                        value={selectedMaterial || null}
                        onChange={(_, v) => updateMaterial(index, 'raw_material_id', v?.id || 0)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Raw Material"
                            size="small"
                            required
                            error={!material.raw_material_id}
                          />
                        )}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      />
                    </Grid>
                    <Grid size={{ xs: 9, md: 4 }}>
                      <TextField
                        label="Quantity (kg)"
                        type="number"
                        value={material.quantity || ''}
                        onChange={(e) =>
                          updateMaterial(index, 'quantity', parseFloat(e.target.value) || 0)
                        }
                        fullWidth
                        size="small"
                        required
                        helperText={
                          topUpTotal > 0
                            ? `+${topUpTotal.toFixed(3)} kg top-up → ${(material.quantity + topUpTotal).toFixed(3)} kg submitted`
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 3, md: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="Remove material">
                        <span>
                          <IconButton
                            onClick={() => removeMaterial(index)}
                            disabled={materials.length === 1}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>

            <TextField
              label="Other Costs (GH₵)"
              type="number"
              value={formData.other_costs}
              onChange={(e) => setFormData({ ...formData, other_costs: e.target.value })}
              fullWidth
              required
              helperText="Utilities, packaging, etc."
            />

            <TextField
              label="Production Date"
              type="date"
              value={formData.production_date}
              onChange={(e) => setFormData({ ...formData, production_date: e.target.value })}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              required
            />

            <TextField
              label="Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
            />
          </Box>

          {/* Cost Summary — sticky at bottom of dialog */}
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              px: 3,
              py: 2,
            }}
          >
            {produceMutation.isError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {(produceMutation.error as any)?.response?.data?.message ||
                  'Failed to produce feed'}
              </Alert>
            )}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Cost Summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Raw Materials
                </Typography>
                <Typography variant="body2">{formatCurrency(totalMaterialCost)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Other Costs
                </Typography>
                <Typography variant="body2">{formatCurrency(formData.other_costs || 0)}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  pt: 0.75,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Total Cost
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(totalProductionCost)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Cost per Bag
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(costPerBag)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog}>Cancel</Button>
          <Button
            onClick={handleProduceFeed}
            variant="contained"
            disabled={
              !formData.feed_type_id ||
              !formData.feed_brand_id ||
              !formData.bird_procurement_id ||
              !formData.quantity_kg ||
              materials.some((m) => !m.raw_material_id || m.quantity <= 0) ||
              produceMutation.isPending ||
              updateMutation.isPending
            }
          >
            {produceMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={24} />
            ) : editingProductionId ? (
              'Save Changes'
            ) : (
              'Produce Feed'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this feed production record? This action cannot be
            undone.
          </Typography>
          {deleteMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(deleteMutation.error as any)?.response?.data?.message ||
                'Failed to delete production'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-assign result snackbar */}
      <Snackbar
        open={autoAssignSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setAutoAssignSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={autoAssignSnackbar.severity}
          onClose={() => setAutoAssignSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {autoAssignSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Production Detail Dialog */}
      <ProductionDetailDialog
        open={detailDialogOpen}
        productionId={selectedProductionId}
        onClose={handleCloseDetail}
      />
    </Box>
  );
};

// ── Detail dialog ──────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 1 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mr: 2, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

const ProductionDetailDialog: React.FC<{
  open: boolean;
  productionId: number;
  onClose: () => void;
}> = ({ open, productionId, onClose }) => {
  const { data: production, isLoading } = useFeedProductionDetail(productionId);

  if (!open) return null;

  const totalMaterialCost = production ? parseFloat(production.total_raw_material_cost) : 0;
  const totalMaterialQty =
    production?.materials?.reduce((sum: number, m: any) => sum + parseFloat(m.quantity_used), 0) ??
    0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Production Details</Typography>
          {production && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {production.production_number}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : production ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
            {/* Hero stats */}
            <Paper
              sx={{
                p: 2.5,
                background: 'linear-gradient(135deg, #424242 0%, #2a2a2a 100%)',
                border: '1px solid #555',
              }}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                    Bags Produced
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: '#ff9800', lineHeight: 1.1 }}
                  >
                    {parseFloat(production.quantity_produced_bags).toFixed(1)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {parseFloat(production.quantity_produced_kg).toFixed(0)} kg
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                    Total Cost
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: '#4caf50', lineHeight: 1.2 }}
                  >
                    {formatCurrency(production.total_production_cost)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                    Cost / Bag
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: '#42a5f5', lineHeight: 1.2 }}
                  >
                    {formatCurrency(production.cost_per_bag)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                    Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#eee', mt: 0.5 }}>
                    {format(new Date(production.production_date), 'MMM dd, yyyy')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    {production.produced_by?.name || '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Feed + metadata */}
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Feed Details
              </Typography>
              <Divider />
              <InfoRow label="Brand" value={production.feed_brand?.name} />
              <Divider />
              <InfoRow label="Type" value={production.feed_type?.name} />
              {production.bird_procurement && (
                <>
                  <Divider />
                  <InfoRow label="Procurement" value={production.bird_procurement?.identifier} />
                </>
              )}
              {production.notes && (
                <>
                  <Divider />
                  <InfoRow label="Notes" value={production.notes} />
                </>
              )}
            </Paper>

            {/* Materials table */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Raw Materials Used
              </Typography>
              <TableContainer
                component={Paper}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Material</TableCell>
                      <TableCell align="right">Qty (kg)</TableCell>
                      <TableCell align="right">Bags</TableCell>
                      <TableCell align="right">Cost / unit</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">% of mix</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {production.materials?.map((material: any) => {
                      const qty = parseFloat(material.quantity_used);
                      const cost = parseFloat(material.total_cost);
                      const kgPerBag = material.raw_material?.default_kg_per_bag
                        ? parseFloat(material.raw_material.default_kg_per_bag)
                        : 50;
                      const bags = qty / kgPerBag;
                      const pct = totalMaterialQty > 0 ? (qty / totalMaterialQty) * 100 : 0;
                      return (
                        <TableRow key={material.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {material.raw_material?.name || 'Unknown'}
                          </TableCell>
                          <TableCell align="right">
                            {qty.toFixed(2)}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              {material.raw_material?.unit_of_measure || 'kg'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{bags.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(material.cost_per_unit)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatCurrency(cost)}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.75,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 4,
                                  borderRadius: 2,
                                  backgroundColor: 'action.hover',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${pct}%`,
                                    height: '100%',
                                    backgroundColor: '#ff9800',
                                    borderRadius: 2,
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ minWidth: 32, textAlign: 'right' }}
                              >
                                {pct.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 700 }}>
                        Total Raw Material Cost
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatCurrency(totalMaterialCost)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Cost summary */}
            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Cost Summary
              </Typography>
              <Divider />
              <InfoRow label="Raw Materials" value={formatCurrency(totalMaterialCost)} />
              <Divider />
              <InfoRow
                label="Other Costs (utilities, packaging)"
                value={formatCurrency(production.other_costs)}
              />
              <Divider />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  py: 1,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  Total
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50' }}>
                  {formatCurrency(production.total_production_cost)}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  py: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Cost per Bag (50 kg)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#ff9800' }}>
                  {formatCurrency(production.cost_per_bag)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Alert severity="error">Failed to load production details</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedProductionPage;
